import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { User, Camera, Pencil, Image as ImageIcon, Plus, Star, Users, Heart } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const [myContent, setMyContent] = useState([]);
  const [mySubscriptions, setMySubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [contentOpen, setContentOpen] = useState(false);
  const [tierOpen, setTierOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [editForm, setEditForm] = useState({
    display_name: user?.display_name || '',
    bio: user?.bio || '',
    avatar_url: user?.avatar_url || '',
    cover_url: user?.cover_url || '',
    gender: user?.gender || '',
    pronouns: user?.pronouns || '',
  });

  const [contentForm, setContentForm] = useState({
    title: '',
    description: '',
    content_type: 'image',
    media_url: '',
    price_tokens: 0,
    is_subscriber_only: false,
    tags: '',
  });

  const [tierForm, setTierForm] = useState({
    name: '',
    price_monthly: 9.99,
    tokens_included: 50,
    free_content_threshold: 10,
    benefits: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (user?.role === 'creator') {
          const contentRes = await axios.get(`${API}/api/content/creator/${user.id}`, { withCredentials: true });
          setMyContent(contentRes.data);
        }
        
        const subsRes = await axios.get(`${API}/api/subscriptions/my`, { withCredentials: true });
        setMySubscriptions(subsRes.data);
      } catch (err) {
        console.error('Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };
    
    if (user) {
      setEditForm({
        display_name: user.display_name || '',
        bio: user.bio || '',
        avatar_url: user.avatar_url || '',
        cover_url: user.cover_url || '',
        gender: user.gender || '',
        pronouns: user.pronouns || '',
      });
      fetchData();
    }
  }, [user]);

  const handleUpdateProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/users/profile`, editForm, { withCredentials: true });
      await refreshUser();
      setEditOpen(false);
      toast.success('Profile updated!');
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateContent = async () => {
    if (!contentForm.title || !contentForm.media_url) {
      toast.error('Title and media URL are required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/api/content`,
        {
          ...contentForm,
          tags: contentForm.tags.split(',').map(t => t.trim()).filter(Boolean),
        },
        { withCredentials: true }
      );
      setMyContent(prev => [response.data, ...prev]);
      setContentOpen(false);
      setContentForm({
        title: '',
        description: '',
        content_type: 'image',
        media_url: '',
        price_tokens: 0,
        is_subscriber_only: false,
        tags: '',
      });
      toast.success('Content published!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create content');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTier = async () => {
    if (!tierForm.name) {
      toast.error('Tier name is required');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/subscriptions/tiers`,
        {
          ...tierForm,
          benefits: tierForm.benefits.split(',').map(b => b.trim()).filter(Boolean),
        },
        { withCredentials: true }
      );
      setTierOpen(false);
      setTierForm({
        name: '',
        price_monthly: 9.99,
        tokens_included: 50,
        free_content_threshold: 10,
        benefits: '',
      });
      toast.success('Subscription tier created!');
    } catch (err) {
      toast.error('Failed to create tier');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Cover & Avatar */}
      <div className="relative mb-20">
        <div 
          className="h-48 md:h-64 rounded-3xl bg-gradient-to-br from-blue-600 to-indigo-600 overflow-hidden"
          style={user.cover_url ? { backgroundImage: `url(${user.cover_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
        >
          <div className="absolute inset-0 bg-black/30" />
        </div>
        
        <div className="absolute -bottom-16 left-6 flex items-end gap-4">
          <div className="relative">
            <div className="w-32 h-32 rounded-full border-4 border-[#09090b] bg-slate-800 overflow-hidden">
              {user.avatar_url ? (
                <img src={user.avatar_url} alt={user.display_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="w-12 h-12 text-slate-500" />
                </div>
              )}
            </div>
            {user.role === 'creator' && (
              <span className="absolute -top-2 -right-2 px-2 py-1 bg-amber-500 text-amber-950 text-xs font-bold rounded-full flex items-center gap-1">
                <Star weight="fill" className="w-3 h-3" />
                Creator
              </span>
            )}
          </div>
          
          <div className="pb-2">
            <h1 className="font-heading text-2xl font-bold text-white">{user.display_name}</h1>
            <p className="text-slate-400">{user.email}</p>
          </div>
        </div>

        {/* Edit Button */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogTrigger asChild>
            <button
              className="absolute top-4 right-4 px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-full font-medium text-sm flex items-center gap-2 hover:bg-white/20 transition-colors"
              data-testid="edit-profile-btn"
            >
              <Pencil className="w-4 h-4" />
              Edit Profile
            </button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Edit Profile</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Display Name</Label>
                <Input
                  value={editForm.display_name}
                  onChange={e => setEditForm({ ...editForm, display_name: e.target.value })}
                  className="bg-slate-800 border-white/10 mt-1"
                />
              </div>
              <div>
                <Label>Bio</Label>
                <Textarea
                  value={editForm.bio}
                  onChange={e => setEditForm({ ...editForm, bio: e.target.value })}
                  className="bg-slate-800 border-white/10 mt-1"
                  rows={3}
                />
              </div>
              <div>
                <Label>Avatar URL</Label>
                <Input
                  value={editForm.avatar_url}
                  onChange={e => setEditForm({ ...editForm, avatar_url: e.target.value })}
                  className="bg-slate-800 border-white/10 mt-1"
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>Cover URL</Label>
                <Input
                  value={editForm.cover_url}
                  onChange={e => setEditForm({ ...editForm, cover_url: e.target.value })}
                  className="bg-slate-800 border-white/10 mt-1"
                  placeholder="https://..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Gender</Label>
                  <select
                    value={editForm.gender}
                    onChange={e => setEditForm({ ...editForm, gender: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-2 mt-1 text-white"
                  >
                    <option value="">Prefer not to say</option>
                    <option value="Man">Man</option>
                    <option value="Woman">Woman</option>
                    <option value="Trans Man">Trans Man</option>
                    <option value="Trans Woman">Trans Woman</option>
                    <option value="Non-binary">Non-binary</option>
                    <option value="Genderqueer">Genderqueer</option>
                    <option value="Genderfluid">Genderfluid</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Pronouns</Label>
                <select
                  value={editForm.pronouns}
                  onChange={e => setEditForm({ ...editForm, pronouns: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-2 mt-1 text-white"
                >
                  <option value="">Select pronouns</option>
                  <option value="he/him">he/him</option>
                  <option value="she/her">she/her</option>
                  <option value="they/them">they/them</option>
                  <option value="he/they">he/they</option>
                  <option value="she/they">she/they</option>
                  <option value="ze/zir">ze/zir</option>
                  <option value="xe/xem">xe/xem</option>
                  <option value="any pronouns">any pronouns</option>
                  <option value="ask me">ask me</option>
                </select>
              </div>
              <button
                onClick={handleUpdateProfile}
                disabled={saving}
                className="w-full py-3 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 transition-all disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="glass-light rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-white">{myContent.length}</div>
          <div className="text-sm text-slate-400">Posts</div>
        </div>
        <div className="glass-light rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-amber-400">{user.tokens}</div>
          <div className="text-sm text-slate-400">Tokens</div>
        </div>
        <div className="glass-light rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-blue-500">{mySubscriptions.length}</div>
          <div className="text-sm text-slate-400">Subscriptions</div>
        </div>
      </div>

      {/* Bio */}
      {user.bio && (
        <div className="mb-8">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-2">About</h2>
          <p className="text-slate-300">{user.bio}</p>
        </div>
      )}

      {/* Creator Actions */}
      {user.role === 'creator' && (
        <div className="flex gap-3 mb-8">
          <Dialog open={contentOpen} onOpenChange={setContentOpen}>
            <DialogTrigger asChild>
              <button
                className="flex-1 py-3 bg-blue-500 text-white rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-blue-600 transition-all"
                data-testid="create-content-btn"
              >
                <Plus className="w-5 h-5" />
                New Post
              </button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Create Content</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={contentForm.title}
                    onChange={e => setContentForm({ ...contentForm, title: e.target.value })}
                    className="bg-slate-800 border-white/10 mt-1"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={contentForm.description}
                    onChange={e => setContentForm({ ...contentForm, description: e.target.value })}
                    className="bg-slate-800 border-white/10 mt-1"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Media URL *</Label>
                  <Input
                    value={contentForm.media_url}
                    onChange={e => setContentForm({ ...contentForm, media_url: e.target.value })}
                    className="bg-slate-800 border-white/10 mt-1"
                    placeholder="https://..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Type</Label>
                    <select
                      value={contentForm.content_type}
                      onChange={e => setContentForm({ ...contentForm, content_type: e.target.value })}
                      className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-2 mt-1 text-white"
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                    </select>
                  </div>
                  <div>
                    <Label>Price (Tokens)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={contentForm.price_tokens}
                      onChange={e => setContentForm({ ...contentForm, price_tokens: parseInt(e.target.value) || 0 })}
                      className="bg-slate-800 border-white/10 mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Tags (comma separated)</Label>
                  <Input
                    value={contentForm.tags}
                    onChange={e => setContentForm({ ...contentForm, tags: e.target.value })}
                    className="bg-slate-800 border-white/10 mt-1"
                    placeholder="hot, exclusive, new"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="subscriber_only"
                    checked={contentForm.is_subscriber_only}
                    onChange={e => setContentForm({ ...contentForm, is_subscriber_only: e.target.checked })}
                    className="w-5 h-5 rounded bg-slate-800 border-white/10"
                  />
                  <label htmlFor="subscriber_only" className="text-sm text-slate-300">Subscribers only</label>
                </div>
                <button
                  onClick={handleCreateContent}
                  disabled={saving}
                  className="w-full py-3 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 transition-all disabled:opacity-50"
                >
                  {saving ? 'Publishing...' : 'Publish'}
                </button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={tierOpen} onOpenChange={setTierOpen}>
            <DialogTrigger asChild>
              <button
                className="flex-1 py-3 bg-amber-500 text-amber-950 rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-amber-600 transition-all"
                data-testid="create-tier-btn"
              >
                <Star className="w-5 h-5" />
                New Tier
              </button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Create Subscription Tier</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Tier Name *</Label>
                  <Input
                    value={tierForm.name}
                    onChange={e => setTierForm({ ...tierForm, name: e.target.value })}
                    className="bg-slate-800 border-white/10 mt-1"
                    placeholder="e.g. VIP, Premium"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Price ($/month)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={tierForm.price_monthly}
                      onChange={e => setTierForm({ ...tierForm, price_monthly: parseFloat(e.target.value) || 0 })}
                      className="bg-slate-800 border-white/10 mt-1"
                    />
                  </div>
                  <div>
                    <Label>Tokens Included</Label>
                    <Input
                      type="number"
                      min="0"
                      value={tierForm.tokens_included}
                      onChange={e => setTierForm({ ...tierForm, tokens_included: parseInt(e.target.value) || 0 })}
                      className="bg-slate-800 border-white/10 mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Free Content Threshold (Tokens)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={tierForm.free_content_threshold}
                    onChange={e => setTierForm({ ...tierForm, free_content_threshold: parseInt(e.target.value) || 0 })}
                    className="bg-slate-800 border-white/10 mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">Content priced at or below this is free for subscribers</p>
                </div>
                <div>
                  <Label>Benefits (comma separated)</Label>
                  <Input
                    value={tierForm.benefits}
                    onChange={e => setTierForm({ ...tierForm, benefits: e.target.value })}
                    className="bg-slate-800 border-white/10 mt-1"
                    placeholder="Direct messages, Exclusive content"
                  />
                </div>
                <button
                  onClick={handleCreateTier}
                  disabled={saving}
                  className="w-full py-3 bg-amber-500 text-amber-950 rounded-full font-semibold hover:bg-amber-600 transition-all disabled:opacity-50"
                >
                  {saving ? 'Creating...' : 'Create Tier'}
                </button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* My Content */}
      {user.role === 'creator' && (
        <div className="mb-8">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">My Content</h2>
          {myContent.length === 0 ? (
            <div className="text-center py-12 glass-light rounded-2xl">
              <ImageIcon className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500">No content yet</p>
              <p className="text-slate-600 text-sm">Create your first post!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {myContent.map(item => (
                <div key={item.id} className="aspect-square rounded-xl overflow-hidden bg-slate-900 relative group">
                  <img src={item.thumbnail_url || item.media_url} alt={item.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="text-center">
                      <Heart className="w-6 h-6 text-white mx-auto mb-1" />
                      <span className="text-white text-sm">{item.likes_count}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* My Subscriptions */}
      <div>
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">My Subscriptions</h2>
        {mySubscriptions.length === 0 ? (
          <div className="text-center py-12 glass-light rounded-2xl">
            <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500">No subscriptions yet</p>
            <p className="text-slate-600 text-sm">Subscribe to your favorite creators!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {mySubscriptions.map(sub => (
              <div key={sub.id} className="flex items-center gap-4 p-4 glass-light rounded-xl">
                <div className="w-12 h-12 rounded-full bg-slate-800 overflow-hidden">
                  {sub.creator_avatar ? (
                    <img src={sub.creator_avatar} alt={sub.creator_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-6 h-6 text-slate-500" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">{sub.creator_name}</p>
                  <p className="text-sm text-slate-500">{sub.tier_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">Expires</p>
                  <p className="text-sm text-slate-300">
                    {new Date(sub.expires_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
